import {useCallback, useEffect, useRef, useState} from 'react';
import {useRouter} from 'next/router';

import {useAppConfig} from '@/contexts/AppConfigContext';
import {useLocation} from '@/contexts/LocationContext';
import {useToastNotificationContext} from '@/contexts/ToastNotificationContext';
import {useAuthenticationServices} from '@/hooks/login/useAuthenticationServices';
import {AUTH_LABELS, ERROR_MESSAGES} from '@/lib/constants/auth';
import {GoogleAuthenticationService} from '@/services/auth/GoogleAuthenticationService';
import {tracking} from '@/services/tracking/TrackingService';

import MainButton from '@/components/ui/buttons/MainButton/MainButton';

type props = {
  mode: 'signUp' | 'signIn';
};

export default function AuthWithGoogle({mode = 'signUp'}: props) {
  const [error, setError] = useState<string>();
  const loadingRef = useRef(false);

  const router = useRouter();

  const {googleService, emailService, userService} = useAuthenticationServices();
  const {setToastNotification} = useToastNotificationContext();
  const {countryCode} = useLocation();
  const {appConfig} = useAppConfig();

  const isSignUpWithGoogleEnabled = appConfig?.signinByGoogle;
  const isSignUp = mode === 'signUp';

  useEffect(() => {
    if (!error) {
      return;
    }

    setToastNotification({
      messageConfig: {
        life: 5000,
        severity: 'error',
        summary: error,
        detail: `${ERROR_MESSAGES.GENERIC} ${AUTH_LABELS[mode]}`,
      },
    });
  }, [error, mode, setToastNotification]);

  const handleGoogleValidation = useCallback(async () => {
    try {
      await googleService.validateIdentifier();
    } catch (error: unknown) {
      googleService.handleAuthenticationError({
        error,
        setError,
        fallbackErrorMsg: GoogleAuthenticationService.GENERIC_ERROR.VALIDATE_USER,
        onUserAlreadyLoggedIn: () => {
          router.push('/home');
        },
      });
      throw error;
    }
  }, [googleService, router]);

  const handleGoogleSignUpOrSignIn = useCallback(async () => {
    try {
      const credentialResponse = await googleService.signUpOrSignInWithPopup();

      const isNewUser = credentialResponse?._tokenResponse?.isNewUser;

      if (!isSignUp && isNewUser) {
        tracking.logEvent(tracking.events.sign_up_initiated());
      }

      if (!isNewUser) {
        router.push('/login');
        return;
      }

      const {
        user: {email},
      } = credentialResponse;

      await userService.initiateSignup({email: email!, countryCode, marketingOptIn: false});
      emailService.saveIdentifier(email!);

      router.push('/signup/pincode');
    } catch (error: unknown) {
      googleService.handleAuthenticationError({
        error,
        setError,
        fallbackErrorMsg: isSignUp
          ? GoogleAuthenticationService.GENERIC_ERROR.SIGN_UP
          : GoogleAuthenticationService.GENERIC_ERROR.SIGN_IN,
      });
    }
  }, [countryCode, emailService, googleService, isSignUp, router, userService]);

  const handleAuthByGoogle = useCallback(async () => {
    loadingRef.current = true;
    googleService.saveLastAuthenticationMethod();

    const trackEvent = isSignUp
      ? tracking.events.click_in_signup_with_google_signup()
      : tracking.events.click_in_signin_with_google_signup();
    tracking.logEvent(trackEvent);

    await handleGoogleValidation();
    await handleGoogleSignUpOrSignIn();

    loadingRef.current = false;
  }, [googleService, handleGoogleSignUpOrSignIn, handleGoogleValidation, isSignUp]);

  if (!isSignUpWithGoogleEnabled) {
    return null;
  }

  return (
    <MainButton
      fullWidth
      leftIcon={{type: 'custom', name: 'google', size: 22}}
      variant="SECONDARY"
      size="SMALL"
      label="Continue with Google"
      aria-label="Continue with Google"
      loading={loadingRef.current}
      onClick={handleAuthByGoogle}
    />
  );
}
